import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@interstate/components/Typography';
import { Button } from '@interstate/components/Button';
import { Alert } from '@interstate/components/Alert';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { Autocomplete } from '@makemydeal/dr-shared-components';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { AutocompleteOptions } from '@makemydeal/dr-shared-types';
import type { EmployeeDetails, EmployeeDetailsSupportedRoles } from '@makemydeal/dr-dash-types';
import { EmployeeFieldRow, RoleHeaderRow, RoleBlock } from './EmployeeDetailSlideOut.style';
import { dealLockHooks } from '../hooks';
import type { RoleFieldHandlers } from './useEmployeeRoleHandlers';

export type EmployeeRoleFieldsProps = {
    role: EmployeeDetailsSupportedRoles;
    title: string;
    employee: EmployeeDetails | undefined;
    invalidFields: Record<string, boolean>;
    hasRoleError: boolean;
    handlers: RoleFieldHandlers;
    showTrailingDivider?: boolean;
};

export const EmployeeRoleFields: FC<EmployeeRoleFieldsProps> = ({
    role,
    title,
    employee,
    invalidFields,
    hasRoleError,
    handlers,
    showTrailingDivider = true
}) => {
    const isLocked = dealLockHooks.useIsDealLocked();
    const isSearchEnabled = useSelector(featureToggleSelectors.enableDealerEmployeeSearch);

    const {
        employeeSearchResults: searchResults,
        loadingFields,
        currentSearchKey,
        clearCounters,
        createSearchHandler,
        createChangeHandler,
        handleEmployeeSelect,
        handleFieldChange,
        handleFieldBlur,
        handleClear
    } = handlers;
    const clearCounter = clearCounters[role] || 0;
    const selectOnFocusSelector = featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap;

    return (
        <RoleBlock>
            {hasRoleError && (
                <Alert type="caution" title="Incomplete Form" data-testid={`incomplete-form-warning-${role}`}>
                    Please complete all required fields for the team member role to save your information.
                </Alert>
            )}
            <RoleHeaderRow>
                <Typography variant="body-md">{title}</Typography>
                <Button
                    buttonStyle="tertiary"
                    size="small"
                    sx={{ fontSize: '16px' }}
                    data-testid={`employee-clear-button-${role}`}
                    aria-label={`Clear ${title}`}
                    disabled={isLocked}
                    onClick={() => handleClear(role)}
                >
                    Clear
                </Button>
            </RoleHeaderRow>
            <EmployeeFieldRow>
                {isSearchEnabled ? (
                    <Autocomplete
                        key={`${role}-firstName-reset-${clearCounter}`}
                        name="firstName"
                        label="First Name"
                        hasError={invalidFields[`${role}-firstName`]}
                        menuOptions={searchResults[`${role}-firstName`] || []}
                        onSearch={createSearchHandler(role, 'firstName')}
                        onChange={createChangeHandler(role, 'firstName')}
                        onSelect={(selectedOption: AutocompleteOptions) => handleEmployeeSelect(role, selectedOption)}
                        isLoading={(loadingFields[`${role}-firstName`] || false) && currentSearchKey === `${role}-firstName`}
                        value={employee?.name.firstName || ''}
                        data-testid={`employee-first-name-${role}`}
                        size="small"
                        selectOnFocusSelector={selectOnFocusSelector}
                    />
                ) : (
                    <TextInput
                        name="firstName"
                        label="First Name"
                        hasError={invalidFields[`${role}-firstName`]}
                        onChange={(evt) => handleFieldChange(role, 'firstName', evt.target.value)}
                        onBlur={(evt) => handleFieldBlur(role, 'firstName', evt.target.value)}
                        value={employee?.name.firstName || ''}
                        data-testid={`employee-first-name-${role}`}
                        disabled={isLocked}
                        size="small"
                        selectOnFocusSelector={selectOnFocusSelector}
                    />
                )}

                {isSearchEnabled ? (
                    <Autocomplete
                        key={`${role}-lastName-reset-${clearCounter}`}
                        name="lastName"
                        label="Last Name"
                        hasError={invalidFields[`${role}-lastName`]}
                        menuOptions={searchResults[`${role}-lastName`] || []}
                        onSearch={createSearchHandler(role, 'lastName')}
                        onChange={createChangeHandler(role, 'lastName')}
                        onSelect={(selectedOption: AutocompleteOptions) => handleEmployeeSelect(role, selectedOption)}
                        isLoading={(loadingFields[`${role}-lastName`] || false) && currentSearchKey === `${role}-lastName`}
                        value={employee?.name.lastName || ''}
                        data-testid={`employee-last-name-${role}`}
                        size="small"
                        selectOnFocusSelector={selectOnFocusSelector}
                    />
                ) : (
                    <TextInput
                        name="lastName"
                        label="Last Name"
                        hasError={invalidFields[`${role}-lastName`]}
                        onChange={(evt) => handleFieldChange(role, 'lastName', evt.target.value)}
                        onBlur={(evt) => handleFieldBlur(role, 'lastName', evt.target.value)}
                        value={employee?.name.lastName || ''}
                        data-testid={`employee-last-name-${role}`}
                        disabled={isLocked}
                        size="small"
                        selectOnFocusSelector={selectOnFocusSelector}
                    />
                )}

                <TextInput
                    name="dmsId"
                    label="DMS ID (optional)"
                    hasError={invalidFields[`${role}-employeeDmsId`]}
                    onChange={(evt) => handleFieldChange(role, 'employeeDmsId', evt.target.value)}
                    onBlur={(evt) => handleFieldBlur(role, 'employeeDmsId', evt.target.value)}
                    value={employee?.employeeDmsId || ''}
                    data-testid={`employee-dms-id-${role}`}
                    disabled={isLocked}
                    size="small"
                    selectOnFocusSelector={selectOnFocusSelector}
                />
            </EmployeeFieldRow>
            {showTrailingDivider && <hr style={{ marginTop: '1rem' }} />}
        </RoleBlock>
    );
};
